import type { CSSProperties, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { accessDeniedNavigateState } from "@/utils/accessDenied";

type ProtectedRouteProps = {
  children: ReactNode;
  /** When set, also requires session.acuityLearn.pageAccess[page]. */
  page?: string;
};

/**
 * Gates a route behind authentication (and optional page_access).
 *
 * Missing page access kicks to home; AppShell shows a floating notice from location state.
 *
 *   <Route path="/protected" element={<ProtectedRoute><Page /></ProtectedRoute>} />
 *   <Route path="/build" element={<ProtectedRoute page="course-builder"><Page /></ProtectedRoute>} />
 */
const ProtectedRoute = ({ children, page }: ProtectedRouteProps) => {
  const { isAuthenticated, hasAccess, pageAccess, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
      </div>
    );
  }

  if (!isAuthenticated || !hasAccess) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  if (page && !pageAccess?.[page]) {
    return (
      <Navigate
        to="/"
        replace
        state={accessDeniedNavigateState(page, location.pathname)}
      />
    );
  }

  return <>{children}</>;
};

const styles = {
  loadingContainer: {
    position: "fixed",
    inset: 0,
    background: "var(--bg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
  },
  loadingSpinner: {
    width: "18px",
    height: "18px",
    border: "2px solid var(--line)",
    borderTop: "2px solid var(--ink)",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
} satisfies Record<string, CSSProperties>;

export default ProtectedRoute;
